import { useQuery } from '@tanstack/react-query';
import { formatISO } from 'date-fns';
import backendApi from '../services/axios.auth.wrapper';

const baseURL = process.env.REACT_APP_BACKEND_URL;

type DateRange = { startDate?: Date | null; endDate?: Date | null } | null | undefined;

const serializeArray = (values: (string | number)[] | null | undefined): string => 
  values?.join('|') ?? '';

const serializeDateRange = (dateRange: DateRange): string => {
  if (!dateRange?.startDate || !dateRange?.endDate) return '';
  return `${formatISO(dateRange.startDate, { representation: 'date' })}_${formatISO(dateRange.endDate, { representation: 'date' })}`;
};

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  keepPreviousData: true,
};

// export const GetLatestSegment = (setDateRange: any) =>
//   useQuery(
//     ['latest-segment'],
//     () => {
//       return backendApi({
//         method: 'get',
//         url: `${baseURL}/api/segments/latest`,
//       });
//     },
//     {
//       onSuccess: (data) => {
//         if (data?.data === '') {
//           const mostRecentDate = new Date();
//           const oneMonthBack = new Date();
//           oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);
//           setDateRange({
//             startDate: oneMonthBack,
//             endDate: new Date(),
//           });
//         } else {
//           const mostRecentDate = new Date(data.data.segment_timestamp);
//           const oneMonthBack = new Date(data.data.segment_timestamp);
//           oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);
//           setDateRange({
//             startDate: oneMonthBack,
//             endDate: new Date(data.data.segment_timestamp),
//           });
//         }
//       },
//     },
//   );



export const GetBirdsAtCollisionRiskCountRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
) =>
  useQuery(
    ['collision-risk-count-range', serializeDateRange(dateRange), site_id, serializeArray(camera_ids)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/collision_risk_date_range`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetBirdCountRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
) =>
  useQuery(
    ['birds-count-range', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_count_date_range`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
          aggregation: 'daily',
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetHoursRecordedRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
) =>
  useQuery(
    ['hours-recorded-range', serializeDateRange(dateRange), site_id, serializeArray(camera_ids)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/hours_recorded_date_range`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          aggregation: 'daily',
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetBirdCountPerHour = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
) =>
  useQuery(
    ['birds-count-per-hour', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_count_per_hour`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetHoursRecordedPerHour = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
) =>
  useQuery(
    ['hours-recorded-per-hour', serializeDateRange(dateRange), site_id, serializeArray(camera_ids)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/total_video_recorded_per_hour`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetSiteData = () =>
  useQuery(
    ['site-data'],
    () => {
      return backendApi({
        method: 'get',
        url: `${baseURL}/api/site`,
      });
    },
    { ...DEFAULT_QUERY_OPTIONS },
  );

export const GetCameraData = (site_id: string) =>
  useQuery(
    ['camera-data', site_id],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/camera`,
        data: {
          site_id,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id },
  );

export const GetSpeciesList = (
  setSpecies: (species: any[]) => void,
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
) => {
  useQuery(
    ['species-list', serializeDateRange(dateRange), site_id, serializeArray(camera_ids)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/species_list`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
        },
      });
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate,
      onSuccess: (data: any) => {
        let result: any[] = [];
        
        if (data?.data && Array.isArray(data.data)) {
          data.data.forEach((element: any) => {
            const speciesOrder = element.species_name__species_order;
            const speciesName = element.species_name__species_name;
            
            if (!speciesOrder && !speciesName) {
              return;
            }
            
            const orderLabel = speciesOrder || 'Unidentified';
            const index = result.findIndex(
              (x: any) => x.label === orderLabel,
            );
            
            if (index === -1) {
              result.push({
                label: orderLabel,
                children: speciesName ? [{ label: speciesName }] : [],
              });
            } else {
              if (speciesName) {
                result[index].children.push({ label: speciesName });
              }
            }
          });
        }
        
        setSpecies(result);
      },
    },
  );
};

export const GetSpeciesDateRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
) =>
  useQuery(
    ['species-date-range', serializeDateRange(dateRange), site_id, serializeArray(camera_ids)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/species_date_range`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetWindSpeedDateRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
) =>
  useQuery(
    ['wind-speed-date-range', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_count_wind_speed`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetBirdCountDirectionDateRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
) =>
  useQuery(
    ['bird-count-direction-date-range', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_count_direction`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetBirdCountWindDirection = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
) =>
  useQuery(
    ['bird-count-wind-direction', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_count_wind_direction`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate },
  );

export const GetBirdHeight = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
  enabled: boolean = true,
) =>
  useQuery(
    ['bird-height', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      const div = document.getElementById('bottom-line');
      if (div) {
        div.remove();
      }
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_height`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate && enabled },
  );

export const GetBirdHeightV2 = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
  enabled: boolean = true,
) =>
  useQuery(
    ['bird_flight_height_v2', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      const div = document.getElementById('bottom-line');
      if (div) {
        div.remove();
      }
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_height_v2`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate && enabled },
  );

export const GetBirdHeightV3 = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[],
  species_name: string[],
  enabled: boolean = true,
) =>
  useQuery(
    ['bird_flight_height_v3', serializeDateRange(dateRange), site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      const div = document.getElementById('bottom-line');
      if (div) {
        div.remove();
      }
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_height_v3`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate && enabled },
  );
